/**
 * NoteRing - the pitch-class selector visual: a single SVG ring of 12
 * wedges with gaps, labels horizontal inside the wedges, and the two
 * ↻/↷ rotate steppers in the center hole.
 *
 * The component owns geometry only: it renders the wedges once and
 * provides the toggle interaction by callback. The
 * selection-related visual states (selected, dimmed-hollow) are pushed
 * in by the caller through `update`.
 *
 * Geometry is frozen from the prototype (see the ring prototype branch,
 * variant C): ring spans innerFrac=0.61..0.96 of the half-viewBox, 1.0deg
 * gap between wedges, labels at the ring's mid-radius. A center hole large
 * enough for two 4.5rem steppers sits at the middle and is handled by the
 * caller's overlay buttons (this component does not draw them).
 *
 * Dual enharmonic labels (e.g. 'C♯/D♭') render inline: the main name at
 * a fixed rem base, the slash + second name at 0.68em, following variant
 * C of the prototype. Font size uses fixed REM so the label never scales
 * with viewport width.
 */

/** Interactive surface the tool wires: toggles go back to the tool. */
interface NoteRingCallbacks {
    onToggle(pc: number): void;
}

const NS = 'http://www.w3.org/2000/svg';
const VIEWBOX = 400;
const CENTER = VIEWBOX / 2;
const OUTER_R = CENTER * 0.96;
const INNER_R = CENTER * 0.61;
const GAP_DEG = 1.0;         // eaten from each wedge side

function polar(radius: number, angleDeg: number): [number, number] {
    const rad = angleDeg * Math.PI / 180;
    return [
        CENTER + radius * Math.sin(rad),
        CENTER - radius * Math.cos(rad),
    ];
}

class NoteRing {

    private readonly callbacks: NoteRingCallbacks;
    private readonly svg: SVGSVGElement;
    /** label list mirrored from the model (used for the aria-labels) */
    private labels: string[];

    constructor(parent: HTMLElement, callbacks: NoteRingCallbacks, labels: string[]) {
        this.callbacks = callbacks;
        this.labels = labels;
        this.svg = document.createElementNS(NS, 'svg');
        this.svg.setAttribute('viewBox', `0 0 ${VIEWBOX} ${VIEWBOX}`);
        this.svg.classList.add('note-ring');
        parent.prepend(this.svg);
        this.build();
    }

    private svgEl<K extends keyof SVGElementTagNameMap>(tag: K): SVGElementTagNameMap[K] {
        return document.createElementNS(NS, tag) as SVGElementTagNameMap[K];
    }

    private wedgePath(pc: number): string {
        const centerAngle = pc * 30;
        const start = centerAngle - 15 + GAP_DEG;
        const end = centerAngle + 15 - GAP_DEG;
        const rO = OUTER_R;
        const rI = INNER_R;
        const [oSx, oSy] = polar(rO, start);
        const [oEx, oEy] = polar(rO, end);
        const [iEx, iEy] = polar(rI, end);
        const [iSx, iSy] = polar(rI, start);
        const f = (n: number) => n.toFixed(2);
        return `M ${f(oSx)} ${f(oSy)} A ${f(rO)} ${f(rO)} 0 0 1 ${f(oEx)} ${f(oEy)}`
            + ` L ${f(iEx)} ${f(iEy)} A ${f(rI)} ${f(rI)} 0 0 0 ${f(iSx)} ${f(iSy)} Z`;
    }

    private buildLabel(parent: SVGGElement, x: number, y: number, label: string) {
        // alphabetic baseline (the default) is shared by the tspans, so the
        // primary and secondary names align on their bottoms. dy=0.35em drops
        // the baseline by half a cap height, visually centering the caps on
        // the wedge's mid-radius point (dominant-baseline: central was the
        // alternative but it aligns middles across mixed font sizes).
        const [main, second] = label.split('/');
        if (!second) {
            const text = this.svgEl('text');
            text.setAttribute('x', x.toFixed(1));
            text.setAttribute('y', y.toFixed(1));
            text.setAttribute('dy', '0.35em');
            text.setAttribute('class', 'ring-label-main');
            text.textContent = main;
            parent.appendChild(text);
            return;
        }
        const text = this.svgEl('text');
        text.setAttribute('x', x.toFixed(1));
        text.setAttribute('y', y.toFixed(1));
        text.setAttribute('dy', '0.35em');
        text.setAttribute('class', 'ring-label-main');
        const primary = this.svgEl('tspan');
        primary.setAttribute('class', 'ring-label-main-part');
        primary.textContent = main;
        const secondary = this.svgEl('tspan');
        secondary.setAttribute('class', 'ring-label-second-part');
        secondary.textContent = `/${second}`;
        text.appendChild(primary);
        text.appendChild(secondary);
        parent.appendChild(text);
    }

    private build() {
        for (let pc = 0; pc < 12; pc++) {
            const wedge = this.svgEl('g');
            wedge.setAttribute('class', 'ring-wedge');
            wedge.dataset.pc = '' + pc;
            wedge.setAttribute('role', 'button');
            wedge.setAttribute('tabindex', '0');
            wedge.setAttribute('aria-label', this.labels[pc]);

            const path = this.svgEl('path');
            path.setAttribute('d', this.wedgePath(pc));
            path.setAttribute('class', 'ring-wedge-path');
            wedge.appendChild(path);

            const midR = (OUTER_R + INNER_R) / 2;
            const [x, y] = polar(midR, pc * 30);
            this.buildLabel(wedge, x, y, this.labels[pc]);

            wedge.addEventListener('click', () => this.callbacks.onToggle(pc));
            wedge.addEventListener('keydown', (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    this.callbacks.onToggle(pc);
                }
            });
            this.svg.appendChild(wedge);
        }
    }

    /** Push selection state into the wedges (the caller computes what's dimmed). */
    update(selected: ReadonlySet<number>, soundedClasses: ReadonlySet<number>) {
        this.svg.querySelectorAll<SVGGElement>('.ring-wedge').forEach(wedge => {
            const pc = +wedge.dataset.pc!;
            const isSelected = selected.has(pc);
            wedge.setAttribute('aria-pressed', '' + isSelected);
            wedge.classList.toggle('ring-dimmed', isSelected && !soundedClasses.has(pc));
        });
    }

}

export {NoteRing};
